import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
  memo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useHistory } from 'react-router-dom';
import {
  Input,
  AutoComplete,
  Button,
  Select,
  Dropdown,
  Typography,
  Space,
  Result,
  Spin,
  Modal,
  Form,
  DatePicker,
  Popover,
  Tooltip,
  message,
  Menu,
} from 'antd';
import {
  ClockCircleOutlined,
  ReloadOutlined,
  PlayCircleFilled,
  CalendarOutlined,
  CopyOutlined,
  MoreOutlined,
  DeleteOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import moment from 'moment-timezone';
import { handleApiSuccess, handleApiError, accessControls } from 'utils';
import {
  timeTrackerProjectsListService,
  timeTrackerEntryGetMyService,
  timeTrackerEntryGetRunningService,
  timeTrackerEntryStartService,
  timeTrackerEntryStopService,
  timeTrackerEntryUpdateService,
  timeTrackerEntryCreateManualService,
  timeTrackerEntryDeleteService,
} from 'services/time_tracker';
import { schedulingMyShiftsForClockService } from 'services/scheduling';
import 'pages/time_tracker/tracker/clockify-tracker.css';
import {
  buildTaskDescriptionSuggestions,
  buildTaskSuggestSelectOptions,
  buildScheduleSuggestSelectOptions,
  filterTaskSuggestOption,
  parseTaskSuggestOptionValue,
  parseScheduleSuggestOptionValue,
} from 'pages/time_tracker/tracker/task_description_suggestions';
import MyScheduleCalendar from 'pages/time_tracker/tracker/my_schedule_calendar';
import {
  getProjectColor,
  getNoProjectColor,
} from 'utils/time_tracker_project_color';
import {
  loadStoredTimezone,
  saveStoredTimezone,
  getEffectiveTimezone,
  getWeekStartMoment,
  isTodayInZone,
  formatWeekHeading,
  getIanaTimezoneOptions,
  getTrackerTimezoneSummary,
} from 'pages/time_tracker/tracker/tracker_timezone';

const { Text } = Typography;

/** One page of the tracker. Older history is another page, not a bigger first load. */
const PAGE_WEEKS = 4;

function formatHistoryRange(oldestWeekStart, newestWeekStart) {
  const end = newestWeekStart.clone().add(6, 'days');
  const startFmt = oldestWeekStart.format(
    oldestWeekStart.year() === end.year() ? 'MMM D' : 'MMM D, YYYY',
  );
  return `${startFmt} – ${end.format('MMM D, YYYY')}`;
}

function formatTimer(seconds) {
  if (seconds == null) return '00:00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(
    s,
  ).padStart(2, '0')}`;
}

function formatElapsed(startedAt) {
  if (!startedAt) return '00:00:00';
  const start = new Date(startedAt).getTime();
  const sec = Math.floor((Date.now() - start) / 1000);
  return formatTimer(sec);
}

/** Ticks on its own so a running clock does not re-render the history list. */
function LiveElapsed({ startedAt, className }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!startedAt) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  return <span className={className}>{formatElapsed(startedAt)}</span>;
}

function formatTimeOnly(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  let h = d.getHours();
  const m = d.getMinutes();
  const am = h < 12;
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, '0')}${am ? 'AM' : 'PM'}`;
}

function formatDuration(seconds) {
  if (seconds == null) return '00:00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(
    s,
  ).padStart(2, '0')}`;
}

function getEntrySeconds(entry) {
  const raw = entry.duration_seconds ?? entry.durationSeconds;
  if (raw != null && raw !== '') {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  if (entry.started_at && entry.stopped_at) {
    return Math.floor(
      (new Date(entry.stopped_at) - new Date(entry.started_at)) / 1000,
    );
  }
  return 0;
}

/** Which calendar day / week an entry belongs to (matches date picker on start). */
function getEntryAnchorDate(entry) {
  if (!entry) return null;
  const raw = entry.started_at || entry.stopped_at;
  return raw ? new Date(raw) : null;
}

/** Newest-first within a day/week; edits to start time reorder the row. */
function compareEntriesByStartDesc(a, b) {
  const ta = new Date(a?.started_at || 0).getTime();
  const tb = new Date(b?.started_at || 0).getTime();
  if (tb !== ta) return tb - ta;
  return String(b._id || '').localeCompare(String(a._id || ''));
}

/** One number = minutes; two parts = MM:SS; three = HH:MM:SS */
function parseDurationToSeconds(input) {
  const s = String(input ?? '').trim();
  if (!s) return null;
  const parts = s.split(':').map((p) => Number(String(p).trim()));
  if (parts.some((n) => !Number.isFinite(n) || n < 0)) return null;
  if (parts.length === 1) return Math.round(parts[0] * 60);
  if (parts.length === 2) return Math.round(parts[0] * 60 + parts[1]);
  return Math.round(parts[0] * 3600 + parts[1] * 60 + (parts[2] || 0));
}

function secondsToDurationInput(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const x = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(
    x,
  ).padStart(2, '0')}`;
}

/**
 * Render the calendar day for an entry, in the tracker's effective timezone so
 * the label matches how the entry is grouped on the page. `short` is used in the
 * inline row chip ("Mon, Apr 20"); `full` is used in the hover tooltip.
 */
function formatEntryDateForTz(dateLike, timeZone, variant = 'short') {
  if (!dateLike) return '';
  const m = timeZone ? moment.tz(dateLike, timeZone) : moment(dateLike);
  if (!m.isValid()) return '';
  if (variant === 'full') {
    return m.isSame(moment(), 'year')
      ? m.format('ddd, MMM D')
      : m.format('ddd, MMM D, YYYY');
  }
  return m.format('ddd, MMM D');
}

/** Merge typed time (11:22AM, 4:20 PM, 16:20) into an existing calendar day. */
function mergeTimeStringIntoMoment(dateMoment, timeStr) {
  const compact = String(timeStr ?? '')
    .trim()
    .replace(/\s+/g, '');
  if (!compact) return null;
  const m = moment(
    compact,
    ['h:mmA', 'hh:mmA', 'h:mm:ssA', 'H:mm', 'HH:mm', 'H:mm:ss', 'HH:mm:ss'],
    true,
  );
  if (!m.isValid()) return null;
  return dateMoment.clone().set({
    hour: m.hour(),
    minute: m.minute(),
    second: m.second(),
    millisecond: 0,
  });
}

/** Project Select uses JSX labels — Ant Design passes label into filterOption as a React node. */
function filterProjectSelectOption(input, option) {
  const q = String(input ?? '')
    .toLowerCase()
    .trim();
  if (!q) return true;
  const haystack =
    typeof option?.searchText === 'string'
      ? option.searchText
      : typeof option?.label === 'string'
      ? option.label.toLowerCase()
      : '';
  return haystack.includes(q);
}

function EditableStoppedEntryRow({
  entry,
  projectOptions,
  taskAutocompleteOptions,
  isRunningThis,
  resumeLoadingEntryId,
  onResume,
  onReload,
  effectiveTz,
}) {
  const entryProjectId = entry.project?._id || entry.project;
  const [rowProjectId, setRowProjectId] = useState(() =>
    entryProjectId ? String(entryProjectId) : undefined,
  );
  const [desc, setDesc] = useState(entry.task_description || '');
  const [mStart, setMStart] = useState(() => moment(entry.started_at));
  const [mStop, setMStop] = useState(() => moment(entry.stopped_at));
  const [startTimeStr, setStartTimeStr] = useState(() =>
    moment(entry.started_at).format('h:mmA'),
  );
  const [endTimeStr, setEndTimeStr] = useState(() =>
    moment(entry.stopped_at).format('h:mmA'),
  );
  const [durStr, setDurStr] = useState(() =>
    secondsToDurationInput(getEntrySeconds(entry)),
  );
  const [saving, setSaving] = useState(false);
  const [calVisible, setCalVisible] = useState(false);

  useEffect(() => {
    setDesc(entry.task_description || '');
    const pid = entry.project?._id || entry.project;
    setRowProjectId(pid ? String(pid) : undefined);
    const ms = moment(entry.started_at);
    const mt = moment(entry.stopped_at);
    setMStart(ms);
    setMStop(mt);
    setStartTimeStr(ms.format('h:mmA'));
    setEndTimeStr(mt.format('h:mmA'));
    setDurStr(secondsToDurationInput(getEntrySeconds(entry)));
  }, [
    entry._id,
    entry.started_at,
    entry.stopped_at,
    entry.duration_seconds,
    entry.task_description,
    entry.project,
  ]);

  const patch = (payload) => {
    setSaving(true);
    return timeTrackerEntryUpdateService(entry._id, payload)
      .then(() => onReload())
      .catch(handleApiError)
      .finally(() => setSaving(false));
  };

  const saveDesc = () => {
    const t = (desc ?? '').trim();
    if (t === (entry.task_description || '').trim()) return;
    return patch({ task_description: t });
  };

  const saveStartTime = () => {
    const next = mergeTimeStringIntoMoment(mStart, startTimeStr);
    if (!next) {
      message.error('Invalid start time. Example: 11:22AM or 4:20 PM');
      setStartTimeStr(mStart.format('h:mmA'));
      return;
    }
    if (next.isSame(mStart)) return;
    if (!next.isBefore(mStop)) {
      message.warning('Start must be before end time');
      setStartTimeStr(mStart.format('h:mmA'));
      return;
    }
    setMStart(next);
    setStartTimeStr(next.format('h:mmA'));
    patch({ started_at: next.toISOString() });
  };

  const saveEndTime = () => {
    const next = mergeTimeStringIntoMoment(mStop, endTimeStr);
    if (!next) {
      message.error('Invalid end time. Example: 4:20PM or 4:20 PM');
      setEndTimeStr(mStop.format('h:mmA'));
      return;
    }
    if (next.isSame(mStop)) return;
    if (!next.isAfter(mStart)) {
      message.warning('End must be after start time');
      setEndTimeStr(mStop.format('h:mmA'));
      return;
    }
    setMStop(next);
    setEndTimeStr(next.format('h:mmA'));
    patch({ stopped_at: next.toISOString() });
  };

  const calendarPanel = (
    <div className="clockify-cal-popover">
      <div className="clockify-cal-popover-hint">
        Shift which calendar day this entry falls on. Start and end clock times
        stay the same (whole entry moves by the number of days you pick).
      </div>
      <DatePicker
        allowClear={false}
        format="MMM D, YYYY"
        className="clockify-cal-single"
        value={mStart.clone().startOf('day')}
        onChange={(d) => {
          if (!d) return;
          const pickedDay = d.clone().startOf('day');
          const prevStartDay = mStart.clone().startOf('day');
          if (pickedDay.isSame(prevStartDay, 'day')) {
            setCalVisible(false);
            return;
          }
          const daysDiff = pickedDay.diff(prevStartDay, 'days');
          const newStart = mStart.clone().add(daysDiff, 'days');
          const newStop = mStop.clone().add(daysDiff, 'days');
          if (!newStop.isAfter(newStart)) {
            message.warning(
              'End must be after start. Adjust times manually if needed.',
            );
            setCalVisible(false);
            return;
          }
          setMStart(newStart);
          setMStop(newStop);
          setStartTimeStr(newStart.format('h:mmA'));
          setEndTimeStr(newStop.format('h:mmA'));
          setCalVisible(false);
          patch({
            started_at: newStart.toISOString(),
            stopped_at: newStop.toISOString(),
          });
        }}
      />
    </div>
  );

  return (
    <div
      className={`clockify-entry-row${
        saving ? ' clockify-entry-row-saving' : ''
      }`}
    >
      <AutoComplete
        className="clockify-entry-desc-autocomplete"
        options={taskAutocompleteOptions}
        filterOption={filterTaskSuggestOption}
        value={desc}
        onChange={(val) => {
          const parsed = parseTaskSuggestOptionValue(val);
          if (parsed) {
            setDesc(parsed.task);
            setRowProjectId(parsed.projectId || undefined);
            patch({
              task_description: parsed.task,
              project: parsed.projectId || null,
            });
            return;
          }
          setDesc(val);
        }}
        dropdownMatchSelectWidth={360}
      >
        <Input
          className="clockify-entry-desc clockify-inline-input"
          bordered={false}
          onBlur={saveDesc}
          onPressEnter={saveDesc}
          placeholder="What are you working on?"
        />
      </AutoComplete>
      <Select
        className="clockify-entry-project-select"
        placeholder="Project"
        value={rowProjectId || undefined}
        onChange={(v) => {
          setRowProjectId(v || undefined);
          patch({ project: v || null });
        }}
        disabled={saving}
        showSearch
        allowClear
        filterOption={filterProjectSelectOption}
        options={projectOptions}
        notFoundContent={
          projectOptions.length === 0 ? 'No projects' : 'No match'
        }
        dropdownMatchSelectWidth={280}
      />
      <span className="clockify-entry-time clockify-entry-time-clockify">
        <Input
          className="clockify-time-box"
          value={startTimeStr}
          onChange={(e) => setStartTimeStr(e.target.value)}
          onBlur={saveStartTime}
          onPressEnter={saveStartTime}
          disabled={saving}
          aria-label="Start time"
        />
        <span className="clockify-entry-time-sep">–</span>
        <Input
          className="clockify-time-box"
          value={endTimeStr}
          onChange={(e) => setEndTimeStr(e.target.value)}
          onBlur={saveEndTime}
          onPressEnter={saveEndTime}
          disabled={saving}
          aria-label="End time"
        />
      </span>
      <span className="clockify-cell-divider" aria-hidden />
      <Popover
        content={calendarPanel}
        title="Pick date"
        trigger="click"
        visible={calVisible}
        onVisibleChange={setCalVisible}
        placement="bottomLeft"
      >
        <Tooltip
          title={`${formatEntryDateForTz(
            mStart,
            effectiveTz,
            'full',
          )} — click to change date`}
          placement="top"
        >
          <button
            type="button"
            className="clockify-entry-date-chip"
            onKeyDown={(e) => e.key === 'Enter' && setCalVisible(true)}
            aria-label={`Change date (currently ${formatEntryDateForTz(
              mStart,
              effectiveTz,
              'full',
            )})`}
          >
            <CalendarOutlined className="clockify-entry-date-chip-icon" />
            <span className="clockify-entry-date-chip-text">
              {formatEntryDateForTz(mStart, effectiveTz, 'short')}
            </span>
          </button>
        </Tooltip>
      </Popover>
      <span className="clockify-cell-divider" aria-hidden />
      <Input
        className="clockify-duration-box"
        title="Duration: HH:MM:SS (or MM:SS). Keeps start time; end shifts to match."
        value={durStr}
        onChange={(e) => setDurStr(e.target.value)}
        onBlur={() => {
          const sec = parseDurationToSeconds(durStr);
          if (sec == null) {
            message.error('Duration: HH:MM:SS, MM:SS, or one number = minutes');
            setDurStr(secondsToDurationInput(getEntrySeconds(entry)));
            return;
          }
          const curSec = Math.max(
            0,
            Math.floor((mStop.valueOf() - mStart.valueOf()) / 1000),
          );
          if (sec === curSec) return;
          patch({ duration_seconds: sec });
        }}
        onPressEnter={(e) => e.target.blur()}
        disabled={saving}
        aria-label="Duration"
      />
      <span className="clockify-cell-divider" aria-hidden />
      {!isRunningThis && (
        <Tooltip title="Start a new timer with this task and project">
          <Button
            type="text"
            icon={
              <PlayCircleFilled
                className="clockify-entry-rerun-icon"
                style={{ color: getProjectColor(entry.project) }}
              />
            }
            className="clockify-entry-rerun"
            loading={resumeLoadingEntryId === entry._id}
            onClick={onResume}
            aria-label="Start new timer for this entry"
          />
        </Tooltip>
      )}
      <span className="clockify-cell-divider" aria-hidden />
      <Dropdown
        overlay={
          <Menu
            onClick={({ key, domEvent }) => {
              if (domEvent && typeof domEvent.stopPropagation === 'function') {
                domEvent.stopPropagation();
              }
              if (key === 'delete') {
                Modal.confirm({
                  title: 'Delete this time entry?',
                  content: 'This cannot be undone.',
                  okText: 'Delete',
                  okType: 'danger',
                  cancelText: 'Cancel',
                  onOk: () =>
                    timeTrackerEntryDeleteService(entry._id)
                      .then((res) => {
                        handleApiSuccess(res);
                        onReload();
                      })
                      .catch(handleApiError),
                });
              }
            }}
            items={[
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Delete',
                danger: true,
              },
            ]}
          />
        }
        trigger={['click']}
      >
        <MoreOutlined className="clockify-entry-more" />
      </Dropdown>
    </div>
  );
}

function EntryDeleteControl({ entryId, onDeleted }) {
  return (
    <Dropdown
      overlay={
        <Menu
          onClick={({ key, domEvent }) => {
            if (domEvent && typeof domEvent.stopPropagation === 'function') {
              domEvent.stopPropagation();
            }
            if (key === 'delete') {
              Modal.confirm({
                title: 'Delete this time entry?',
                content: 'This cannot be undone.',
                okText: 'Delete',
                okType: 'danger',
                cancelText: 'Cancel',
                onOk: () =>
                  timeTrackerEntryDeleteService(entryId)
                    .then((res) => {
                      handleApiSuccess(res);
                      onDeleted();
                    })
                    .catch(handleApiError),
              });
            }
          }}
          items={[
            {
              key: 'delete',
              icon: <DeleteOutlined />,
              label: 'Delete',
              danger: true,
            },
          ]}
        />
      }
      trigger={['click']}
    >
      <MoreOutlined
        className="clockify-entry-more"
        onClick={(e) => e.stopPropagation()}
      />
    </Dropdown>
  );
}

/**
 * Plain row until the user edits it. Mounting an autocomplete and project
 * select on every history row is what made typing and Start/Stop stall.
 */
function StaticStoppedEntryRow({
  entry,
  effectiveTz,
  isRunningThis,
  resumeLoading,
  onActivate,
  onResume,
  onDeleted,
}) {
  const project =
    entry.project && typeof entry.project === 'object' ? entry.project : null;
  const projectName = project?.project_name || 'Project';
  const dot = project ? getProjectColor(project) : getNoProjectColor();
  const dateShort = formatEntryDateForTz(entry.started_at, effectiveTz, 'short');
  const dateFull = formatEntryDateForTz(entry.started_at, effectiveTz, 'full');
  return (
    <div className="clockify-entry-row">
      <button type="button" className="clockify-static-desc" onClick={onActivate}>
        {entry.task_description || 'What are you working on?'}
      </button>
      <button
        type="button"
        className={
          'clockify-static-project' +
          (project ? '' : ' clockify-static-project-empty')
        }
        onClick={onActivate}
      >
        <span className="clockify-project-dot" style={{ background: dot }} />
        <span>{projectName}</span>
      </button>
      <span className="clockify-entry-time clockify-entry-time-clockify">
        <button type="button" className="clockify-static-time" onClick={onActivate}>
          {moment(entry.started_at).format('h:mmA')}
        </button>
        <span className="clockify-entry-time-sep">–</span>
        <button type="button" className="clockify-static-time" onClick={onActivate}>
          {moment(entry.stopped_at).format('h:mmA')}
        </button>
      </span>
      <span className="clockify-cell-divider" aria-hidden />
      <button
        type="button"
        className="clockify-entry-date-chip"
        title={`${dateFull} — click to change date`}
        onClick={onActivate}
      >
        <CalendarOutlined className="clockify-entry-date-chip-icon" />
        <span className="clockify-entry-date-chip-text">{dateShort}</span>
      </button>
      <span className="clockify-cell-divider" aria-hidden />
      <button
        type="button"
        className="clockify-static-duration"
        title="Duration: HH:MM:SS (or MM:SS). Keeps start time; end shifts to match."
        onClick={onActivate}
      >
        {secondsToDurationInput(getEntrySeconds(entry))}
      </button>
      <span className="clockify-cell-divider" aria-hidden />
      {!isRunningThis && (
        <Tooltip title="Start a new timer with this task and project">
          <Button
            type="text"
            icon={
              <PlayCircleFilled
                className="clockify-entry-rerun-icon"
                style={{ color: getProjectColor(entry.project) }}
              />
            }
            className="clockify-entry-rerun"
            loading={resumeLoading}
            onClick={(e) => {
              e.stopPropagation();
              onResume();
            }}
            aria-label="Start new timer for this entry"
          />
        </Tooltip>
      )}
      <span className="clockify-cell-divider" aria-hidden />
      <EntryDeleteControl entryId={entry._id} onDeleted={onDeleted} />
    </div>
  );
}

const StoppedEntryRow = memo(function StoppedEntryRow({
  entry,
  isRunningThis,
  resumeLoading,
  onResume,
  onReload,
  effectiveTz,
  optionsRef,
}) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    const { projectOptions, taskAutocompleteOptions } = optionsRef.current;
    return (
      <EditableStoppedEntryRow
        entry={entry}
        projectOptions={projectOptions}
        taskAutocompleteOptions={taskAutocompleteOptions}
        isRunningThis={isRunningThis}
        resumeLoadingEntryId={resumeLoading ? entry._id : null}
        onResume={() => onResume(entry)}
        onReload={onReload}
        effectiveTz={effectiveTz}
      />
    );
  }
  return (
    <StaticStoppedEntryRow
      entry={entry}
      effectiveTz={effectiveTz}
      isRunningThis={isRunningThis}
      resumeLoading={resumeLoading}
      onActivate={() => setEditing(true)}
      onResume={() => onResume(entry)}
      onDeleted={onReload}
    />
  );
});

/**
 * Owns the description text so each keystroke does not re-render the history.
 * Parent reads the current text through the ref when starting or stopping.
 */
const TimerDescriptionField = forwardRef(function TimerDescriptionField(
  {
    entryId,
    serverText,
    pending,
    taskAutocompleteOptions,
    todayShiftCount,
    onSaveDescription,
    onEnter,
    onClearLink,
    onPickSchedule,
    onPickTask,
  },
  ref,
) {
  const [description, setDescription] = useState(serverText || '');
  const descriptionRef = useRef(description);
  const seenIdRef = useRef(undefined);

  const commitLocal = (text) => {
    const next = text ?? '';
    descriptionRef.current = next;
    setDescription(next);
  };

  useImperativeHandle(ref, () => ({
    get: () => descriptionRef.current,
    set: (text) => commitLocal(text ?? ''),
  }));

  useEffect(() => {
    const prev = seenIdRef.current;
    if (prev === entryId) return;
    seenIdRef.current = entryId;
    if (!entryId) return;
    if (!prev || String(prev).startsWith('pending-')) {
      if (!descriptionRef.current) commitLocal(serverText || '');
      return;
    }
    commitLocal(serverText || '');
  }, [entryId, serverText]);

  useEffect(() => {
    if (!entryId || pending) return undefined;
    if (description === (serverText ?? '')) return undefined;
    const handle = setTimeout(() => {
      onSaveDescription(entryId, description);
    }, 600);
    return () => clearTimeout(handle);
  }, [description, entryId, pending, serverText, onSaveDescription]);

  const handleChange = (val) => {
    const scheduled = parseScheduleSuggestOptionValue(val);
    if (scheduled) {
      commitLocal(scheduled.task);
      onPickSchedule(scheduled);
      return;
    }
    const parsed = parseTaskSuggestOptionValue(val);
    if (parsed) {
      commitLocal(parsed.task);
      onPickTask(parsed);
      return;
    }
    commitLocal(val);
    onClearLink();
  };

  return (
    <AutoComplete
      value={description}
      onChange={handleChange}
      options={taskAutocompleteOptions}
      filterOption={filterTaskSuggestOption}
      defaultActiveFirstOption={false}
      dropdownMatchSelectWidth={420}
      dropdownStyle={{ maxHeight: 320 }}
    >
      <Input
        className="clockify-description-input"
        placeholder={
          todayShiftCount
            ? 'What are you working on? (schedule tasks listed first)'
            : 'What are you working on?'
        }
        onPressEnter={onEnter}
      />
    </AutoComplete>
  );
});

export default function TimeTrackerPage() {
  const history = useHistory();
  const [permissions, setPermissions] = useState(null);
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([]);
  const [runningEntry, setRunningEntry] = useState(null);
  const runningEntryRef = useRef(null);
  runningEntryRef.current = runningEntry;
  const descriptionBridge = useRef(null);
  const startGenerationRef = useRef(0);
  const startInFlightRef = useRef(null);
  const stopLockRef = useRef(false);
  const listOptionsRef = useRef({
    projectOptions: [],
    taskAutocompleteOptions: [],
  });
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [timerStarting, setTimerStarting] = useState(false);
  const [resumeLoadingEntryId, setResumeLoadingEntryId] = useState(null);
  const [manualVisible, setManualVisible] = useState(false);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [stopSubmitting, setStopSubmitting] = useState(false);
  const pendingTaskRef = useRef(null);
  const hydratedRunningIdRef = useRef(null);
  const [manualForm] = Form.useForm();
  const [headerRefreshLoading, setHeaderRefreshLoading] = useState(false);
  const [startTimePopoverOpen, setStartTimePopoverOpen] = useState(false);
  const [runningStartTimeStr, setRunningStartTimeStr] = useState('12:00PM');
  const [savingRunningStart, setSavingRunningStart] = useState(false);
  const [trackerTimezoneStored, setTrackerTimezoneStored] = useState(() =>
    loadStoredTimezone(),
  );
  const [tzSummaryTick, setTzSummaryTick] = useState(0);
  const [shiftPickerVisible, setShiftPickerVisible] = useState(false);
  const [clockShifts, setClockShifts] = useState([]);
  const [selectedClockShiftId, setSelectedClockShiftId] = useState('');
  const [myShifts, setMyShifts] = useState([]);
  const [linkedShiftId, setLinkedShiftId] = useState(null);
  const [scheduleCalendarVisible, setScheduleCalendarVisible] = useState(false);
  const [scheduleWeekStart, setScheduleWeekStart] = useState(null);
  const [startingFromShiftId, setStartingFromShiftId] = useState(null);
  /** 0 = the current 4 weeks. Each step back is the previous 4 weeks. */
  const [historyPage, setHistoryPage] = useState(0);

  const effectiveTz = useMemo(
    () => getEffectiveTimezone(trackerTimezoneStored),
    [trackerTimezoneStored],
  );

  const timezoneSelectOptions = useMemo(() => {
    const guess = moment.tz.guess();
    return [
      {
        value: '',
        label: `Browser default (${guess})`,
      },
      ...getIanaTimezoneOptions(),
    ];
  }, []);

  const trackerTzSummary = useMemo(
    () => getTrackerTimezoneSummary(effectiveTz),
    [effectiveTz, tzSummaryTick],
  );

  const trackerTzCompact = useMemo(() => {
    const tz = effectiveTz;
    const city = (tz.split('/').pop() || tz).replace(/_/g, ' ');
    const abbr = moment.tz(moment(), tz).format('z');
    return `${city} · ${abbr}`;
  }, [effectiveTz, tzSummaryTick]);

  useEffect(() => {
    const id = setInterval(() => setTzSummaryTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    accessControls
      .fetch()
      .then(setPermissions)
      .catch(() => setPermissions({}));
  }, []);

  const loadProjects = () =>
    timeTrackerProjectsListService()
      .then((res) => setProjects(res.data?.items || []))
      .catch(() => setProjects([]));

  const loadRunning = () =>
    timeTrackerEntryGetRunningService()
      .then((res) => {
        const items = res.data?.items || [];
        setRunningEntry(items.length ? items[0] : null);
      })
      .catch(() => setRunningEntry(null));

  const historyWindow = useMemo(() => {
    const currentWeekStart = getWeekStartMoment(new Date(), effectiveTz);
    const newestWeekStart = currentWeekStart
      .clone()
      .subtract(7 * historyPage * PAGE_WEEKS, 'days');
    const oldestWeekStart = newestWeekStart
      .clone()
      .subtract(7 * (PAGE_WEEKS - 1), 'days');
    return { currentWeekStart, newestWeekStart, oldestWeekStart };
  }, [effectiveTz, historyPage]);

  const historyRangeLabel = formatHistoryRange(
    historyWindow.oldestWeekStart,
    historyWindow.newestWeekStart,
  );

  /** @param {{ showListLoading?: boolean }} [opts] */
  const loadMyEntries = (opts = {}) => {
    const showListLoading = opts.showListLoading !== false;
    if (showListLoading) setEntriesLoading(true);
    const { newestWeekStart, oldestWeekStart } = historyWindow;
    const params = {
      limit: 500,
      dateFrom: oldestWeekStart.toISOString(),
    };
    // Current page stays open-ended so a timer started this minute is included.
    if (historyPage > 0) {
      params.dateTo = newestWeekStart
        .clone()
        .add(7, 'days')
        .subtract(1, 'millisecond')
        .toISOString();
    }
    return timeTrackerEntryGetMyService(params)
      .then((res) => {
        const data = res.data;
        const list = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
          ? data
          : [];
        setEntries(list);
      })
      .catch(() => {
        setEntries([]);
      })
      .finally(() => {
        if (showListLoading) setEntriesLoading(false);
      });
  };

  const loadMyEntriesRef = useRef(loadMyEntries);
  loadMyEntriesRef.current = loadMyEntries;
  const reloadEntriesQuiet = useCallback(() => {
    loadMyEntriesRef.current({ showListLoading: false });
  }, []);

  const loadMyShifts = useCallback(
    (weekStartArg) => {
      if (
        !permissions ||
        !accessControls.can(permissions, 'Scheduling.read:any')
      ) {
        setMyShifts([]);
        return Promise.resolve();
      }
      const ws =
        weekStartArg ||
        scheduleWeekStart ||
        getWeekStartMoment(moment(), effectiveTz);
      return schedulingMyShiftsForClockService({
        from: ws.clone().toISOString(),
        to: ws.clone().add(7, 'days').toISOString(),
      })
        .then((res) => setMyShifts(res.data?.items || []))
        .catch(() => setMyShifts([]));
    },
    [permissions, scheduleWeekStart, effectiveTz],
  );

  const refreshAllTrackerData = () => {
    setHeaderRefreshLoading(true);
    Promise.all([
      loadProjects(),
      loadRunning(),
      loadMyEntries({ showListLoading: false }),
      loadMyShifts(scheduleWeekStart),
    ])
      .catch(() => {})
      .finally(() => setHeaderRefreshLoading(false));
  };

  useEffect(() => {
    if (permissions == null) return;
    if (!accessControls.can(permissions, 'TimeTracker.read:any')) return;
    loadProjects();
    loadRunning();
    loadMyEntries();
  }, [permissions, historyPage, effectiveTz]);

  useEffect(() => {
    if (permissions == null) return;
    if (!accessControls.can(permissions, 'Scheduling.read:any')) {
      setMyShifts([]);
      setScheduleWeekStart(null);
      return;
    }
    const ws = getWeekStartMoment(moment(), effectiveTz);
    setScheduleWeekStart(ws);
  }, [permissions, effectiveTz]);

  useEffect(() => {
    if (!scheduleWeekStart) return;
    loadMyShifts(scheduleWeekStart);
  }, [scheduleWeekStart, loadMyShifts]);

  const todayShifts = useMemo(() => {
    const todayKey = moment.tz(effectiveTz).format('YYYY-MM-DD');
    return myShifts.filter(
      (s) =>
        moment.tz(s.starts_at, effectiveTz).format('YYYY-MM-DD') === todayKey,
    );
  }, [myShifts, effectiveTz]);

  const linkedShift = useMemo(
    () =>
      linkedShiftId
        ? myShifts.find((s) => String(s._id) === String(linkedShiftId)) ||
          null
        : null,
    [linkedShiftId, myShifts],
  );
  useEffect(() => {
    if (!runningEntry) setStartTimePopoverOpen(false);
  }, [runningEntry]);

  useEffect(() => {
    if (!startTimePopoverOpen || !runningEntry?.started_at) return;
    setRunningStartTimeStr(moment(runningEntry.started_at).format('h:mmA'));
  }, [startTimePopoverOpen, runningEntry?._id, runningEntry?.started_at]);

  useEffect(() => {
    if (!runningEntry) {
      hydratedRunningIdRef.current = null;
      return;
    }
    if (hydratedRunningIdRef.current !== runningEntry._id) {
      hydratedRunningIdRef.current = runningEntry._id;
      const rid = runningEntry.project?._id || runningEntry.project;
      setSelectedProjectId(rid ? String(rid) : null);
    }
  }, [runningEntry]);

  const { weeksGrouped, rangeTotalSeconds, todayTotalSeconds } =
    useMemo(() => {
      const { currentWeekStart, newestWeekStart, oldestWeekStart } =
        historyWindow;
      const weekBuckets = [];
      const weekIndexByStartMs = new Map();

      for (let i = 0; i < PAGE_WEEKS; i += 1) {
        const ws = newestWeekStart.clone().subtract(7 * i, 'days');
        const key = ws.valueOf();
        weekBuckets.push({
          weekStartMs: key,
          entries: [],
          totalSeconds: 0,
          heading: formatWeekHeading(ws.clone(), currentWeekStart.clone()),
        });
        weekIndexByStartMs.set(key, i);
      }

      let todaySec = 0;

      entries.forEach((e) => {
        const date = getEntryAnchorDate(e);
        if (!date) return;
        const entryWs = getWeekStartMoment(date, effectiveTz);
        if (
          entryWs.isBefore(oldestWeekStart) ||
          entryWs.isAfter(newestWeekStart)
        ) {
          return;
        }
        const idx = weekIndexByStartMs.get(entryWs.valueOf());
        if (idx == null) return;
        const sec = getEntrySeconds(e);
        weekBuckets[idx].entries.push(e);
        weekBuckets[idx].totalSeconds += sec;
        if (isTodayInZone(date, effectiveTz)) todaySec += sec;
      });

      if (runningEntry) {
        const runDate = new Date(runningEntry.started_at);
        const runSec = getEntrySeconds(runningEntry);
        const entryWs = getWeekStartMoment(runDate, effectiveTz);
        if (
          !entryWs.isBefore(oldestWeekStart) &&
          !entryWs.isAfter(newestWeekStart)
        ) {
          const idx = weekIndexByStartMs.get(entryWs.valueOf());
          if (idx != null) {
            weekBuckets[idx].totalSeconds += runSec;
            if (isTodayInZone(runDate, effectiveTz)) todaySec += runSec;
          }
        }
      }

      const weeksGrouped = weekBuckets.map((w) => {
        w.entries.sort(compareEntriesByStartDesc);
        return w;
      });

      const rangeTotalSeconds = weeksGrouped.reduce(
        (s, w) => s + w.totalSeconds,
        0,
      );

      return {
        weeksGrouped,
        rangeTotalSeconds,
        todayTotalSeconds: todaySec,
      };
    }, [entries, runningEntry, effectiveTz, historyWindow]);

  const applyStartedEntry = (generation, created) => {
    if (created) {
      if (startGenerationRef.current === generation) {
        runningEntryRef.current = created;
        setRunningEntry(created);
      }
      return Promise.resolve(created);
    }
    return timeTrackerEntryGetRunningService()
      .then((res) => {
        const items = res.data?.items || [];
        const entry = items.length ? items[0] : null;
        if (startGenerationRef.current === generation) {
          runningEntryRef.current = entry;
          setRunningEntry(entry);
        }
        return entry;
      })
      .catch(() => null);
  };

  const startTimer = (shift) => {
    if (stopLockRef.current || runningEntryRef.current) return;
    const activityName = shift?.activity?.name || '';
    const typed = (descriptionBridge.current?.get?.() ?? '').trim();
    const taskText = (typed || activityName || '').trim();
    if (taskText) {
      pendingTaskRef.current = { task_description: taskText };
    } else {
      pendingTaskRef.current = null;
    }
    if (shift?._id) setStartingFromShiftId(shift._id);
    const generation = startGenerationRef.current + 1;
    startGenerationRef.current = generation;
    const projectObj = selectedProjectId
      ? projects.find((p) => String(p._id) === String(selectedProjectId)) || {
          _id: selectedProjectId,
        }
      : null;
    const optimistic = {
      _id: `pending-${generation}`,
      pending: true,
      started_at: new Date().toISOString(),
      stopped_at: null,
      task_description: taskText,
      task_title: '',
      project: projectObj,
      schedule_shift: shift?._id || null,
    };
    runningEntryRef.current = optimistic;
    setRunningEntry(optimistic);
    if (activityName && !typed) {
      descriptionBridge.current?.set?.(activityName);
    }
    setTimerStarting(true);
    const req = timeTrackerEntryStartService({
      ...(selectedProjectId ? { project: selectedProjectId } : {}),
      task_description: taskText,
      ...(shift
        ? {
            schedule_shift: shift._id,
            schedule_activity: shift.activity?._id || shift.activity,
          }
        : {}),
    })
      .then((res) => {
        setTimerStarting(false);
        setStartingFromShiftId(null);
        setShiftPickerVisible(false);
        setScheduleCalendarVisible(false);
        setLinkedShiftId(null);
        const created = Array.isArray(res.data?.items) ? res.data.items[0] : null;
        handleApiSuccess(res);
        loadMyShifts(scheduleWeekStart);
        return applyStartedEntry(generation, created);
      })
      .catch((err) => {
        setTimerStarting(false);
        setStartingFromShiftId(null);
        if (startGenerationRef.current === generation) {
          runningEntryRef.current = null;
          setRunningEntry(null);
        }
        handleApiError(err);
        const wrapped = err || new Error('Timer did not start');
        wrapped.alreadyShown = true;
        throw wrapped;
      });
    startInFlightRef.current = req;
    return req;
  };

  const handleStart = () => {
    if (linkedShift) {
      startTimer(linkedShift);
      return;
    }
    startTimer();
  };

  const startFromScheduledShift = (shift) => {
    if (runningEntryRef.current || !shift) return;
    const activityName = shift.activity?.name || '';
    if (activityName) descriptionBridge.current?.set?.(activityName);
    setLinkedShiftId(String(shift._id));
    startTimer(shift);
  };

  /**
   * Stop the running timer immediately — no confirmation popup. Whatever is
   * currently in the description input is saved to the entry, and if the
   * timer was started without a project we fall back to whatever the user
   * has picked in the timer-bar project select. Users can always edit the
   * entry afterwards in the list below.
   */
  const handleStopClick = () => {
    const current = runningEntryRef.current;
    if (!current || stopLockRef.current) return;
    const taskDescription = (descriptionBridge.current?.get?.() ?? '').trim();
    const hadProject = !!(current.project?._id || current.project);
    const fallbackProject =
      !hadProject && selectedProjectId ? selectedProjectId : null;
    const linkedToSchedule = !!(
      current.schedule_shift || current.schedule_activity
    );
    if (!hadProject && !fallbackProject && !linkedToSchedule && !current.pending) {
      message.error(
        'Select a project before stopping a timer that was started without one.',
      );
      return;
    }
    const snapshot = current;
    stopLockRef.current = true;
    startGenerationRef.current += 1;
    runningEntryRef.current = null;
    setStopSubmitting(true);
    setRunningEntry(null);
    pendingTaskRef.current = null;
    hydratedRunningIdRef.current = null;
    descriptionBridge.current?.set?.('');
    setSelectedProjectId(null);
    setLinkedShiftId(null);
    setStartTimePopoverOpen(false);

    const optimisticStopped = snapshot.pending
      ? null
      : {
          ...snapshot,
          pending: false,
          stopped_at: new Date().toISOString(),
          duration_seconds: Math.max(
            0,
            Math.floor(
              (Date.now() - new Date(snapshot.started_at).getTime()) / 1000,
            ),
          ),
          task_description: taskDescription,
          ...(fallbackProject && !hadProject
            ? {
                project:
                  projects.find(
                    (p) => String(p._id) === String(fallbackProject),
                  ) || { _id: fallbackProject },
              }
            : {}),
        };
    if (optimisticStopped) {
      setEntries((prev) => [
        optimisticStopped,
        ...prev.filter((e) => String(e._id) !== String(snapshot._id)),
      ]);
    }

    const applyStopped = (entry) =>
      timeTrackerEntryStopService(entry._id, {
        task_title: '',
        task_description: taskDescription,
        ...(fallbackProject ? { project: fallbackProject } : {}),
      }).then((res) => {
        const stopped = Array.isArray(res.data?.items) ? res.data.items[0] : null;
        if (stopped) {
          setEntries((prev) => [
            stopped,
            ...prev.filter(
              (e) =>
                String(e._id) !== String(stopped._id) &&
                String(e._id) !== String(snapshot._id),
            ),
          ]);
        }
        handleApiSuccess(res);
      });

    const ready = snapshot.pending
      ? (startInFlightRef.current || Promise.resolve(null)).then((created) => {
          if (!created?._id || String(created._id).startsWith('pending-')) {
            throw new Error('Timer did not start');
          }
          return created;
        })
      : Promise.resolve(snapshot);

    ready
      .then((entry) =>
        applyStopped(entry).catch((err) => {
          runningEntryRef.current = entry;
          setRunningEntry(entry);
          descriptionBridge.current?.set?.(
            entry.task_description || taskDescription || '',
          );
          const rid = entry.project?._id || entry.project;
          setSelectedProjectId(rid ? String(rid) : fallbackProject);
          if (optimisticStopped) {
            setEntries((prev) =>
              prev.filter((e) => String(e._id) !== String(snapshot._id)),
            );
          }
          throw err;
        }),
      )
      .catch((err) => {
        if (!err?.alreadyShown) handleApiError(err);
      })
      .finally(() => {
        stopLockRef.current = false;
        setStopSubmitting(false);
      });
  };

  const handleResumeEntry = useCallback((entry) => {
    if (stopLockRef.current || runningEntryRef.current) return;
    const pid = entry.project?._id || entry.project;
    if (entry.task_description) {
      pendingTaskRef.current = { task_description: entry.task_description };
    } else {
      pendingTaskRef.current = null;
    }
    const generation = startGenerationRef.current + 1;
    startGenerationRef.current = generation;
    const optimistic = {
      _id: `pending-${generation}`,
      pending: true,
      started_at: new Date().toISOString(),
      stopped_at: null,
      task_description: entry.task_description || '',
      task_title: '',
      project: entry.project || null,
    };
    runningEntryRef.current = optimistic;
    setRunningEntry(optimistic);
    descriptionBridge.current?.set?.(entry.task_description || '');
    setSelectedProjectId(pid ? String(pid) : null);
    setResumeLoadingEntryId(entry._id);
    const req = timeTrackerEntryStartService({
      ...(pid ? { project: String(pid) } : {}),
      task_description: entry.task_description || '',
    })
      .then((res) => {
        setResumeLoadingEntryId(null);
        const created = Array.isArray(res.data?.items) ? res.data.items[0] : null;
        handleApiSuccess(res);
        return applyStartedEntry(generation, created);
      })
      .catch((err) => {
        setResumeLoadingEntryId(null);
        if (startGenerationRef.current === generation) {
          runningEntryRef.current = null;
          setRunningEntry(null);
        }
        handleApiError(err);
        const wrapped = err || new Error('Timer did not start');
        wrapped.alreadyShown = true;
        throw wrapped;
      });
    startInFlightRef.current = req;
  }, []);

  const handleManualSubmit = () => {
    manualForm
      .validateFields()
      .then((values) => {
        const started_at =
          values.started_at?.toISOString?.() ??
          (values.started_at && new Date(values.started_at).toISOString());
        const stopped_at =
          values.stopped_at?.toISOString?.() ??
          (values.stopped_at && new Date(values.stopped_at).toISOString());
        if (!started_at || !stopped_at) {
          message.error('Start and stop time required');
          return;
        }
        if (new Date(stopped_at) < new Date(started_at)) {
          message.error('Stop time must be after start time');
          return;
        }
        const projectId = selectedProjectId;
        if (!projectId) {
          message.info('Select a project first');
          return;
        }
        setManualSubmitting(true);
        timeTrackerEntryCreateManualService({
          project: projectId,
          started_at,
          stopped_at,
          task_title: '',
          task_description: (values.task_description ?? '').trim(),
        })
          .then((res) => {
            setManualSubmitting(false);
            setManualVisible(false);
            manualForm.resetFields();
            handleApiSuccess(res);
            loadMyEntries();
          })
          .catch((err) => {
            setManualSubmitting(false);
            handleApiError(err);
          });
      })
      .catch(() => {});
  };

  const projectOptions = useMemo(() => {
    return [...projects]
      .sort((a, b) =>
        (a.project_name || '').localeCompare(b.project_name || '', undefined, {
          sensitivity: 'base',
        }),
      )
      .map((p) => {
        const displayName = String(p.project_name ?? p._id ?? '');
        return {
          value: p._id,
          searchText: displayName.toLowerCase(),
          label: (
            <span
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <span
                className="clockify-project-dot"
                style={{ background: getProjectColor(p) }}
              />
              <span>{displayName}</span>
            </span>
          ),
        };
      });
  }, [projects]);

  const projectsById = useMemo(() => {
    const m = new Map();
    projects.forEach((p) => {
      if (p?._id != null) m.set(String(p._id), p);
    });
    return m;
  }, [projects]);

  const taskDescSuggestions = useMemo(
    () => buildTaskDescriptionSuggestions(entries, runningEntry),
    [entries, runningEntry],
  );

  const taskAutocompleteOptions = useMemo(() => {
    const recent = buildTaskSuggestSelectOptions(
      taskDescSuggestions,
      projectsById,
    );
    const scheduled = buildScheduleSuggestSelectOptions(
      todayShifts,
      effectiveTz,
    );
    return [...scheduled, ...recent];
  }, [taskDescSuggestions, projectsById, todayShifts, effectiveTz]);

  listOptionsRef.current = { projectOptions, taskAutocompleteOptions };

  const saveRunningDescription = useCallback((entryId, text) => {
    if (!entryId || String(entryId).startsWith('pending-')) return;
    timeTrackerEntryUpdateService(entryId, { task_description: text })
      .then((res) => {
        const items = res.data?.items;
        const updated = Array.isArray(items) ? items[0] : null;
        if (!updated) return;
        setRunningEntry((prev) => {
          if (!prev || String(prev._id) !== String(entryId)) return prev;
          const live = descriptionBridge.current?.get?.() ?? text;
          if (live !== text) return prev;
          return { ...prev, ...updated };
        });
      })
      .catch(handleApiError);
  }, []);

  const clearLinkedShift = useCallback(() => {
    setLinkedShiftId((id) => (id ? null : id));
  }, []);

  const onPickSchedule = useCallback((scheduled) => {
    setLinkedShiftId(scheduled.shiftId);
  }, []);

  const onPickTask = useCallback((parsed) => {
    setSelectedProjectId(parsed.projectId);
    setLinkedShiftId(null);
    const running = runningEntryRef.current;
    if (running?._id && !running.pending) {
      timeTrackerEntryUpdateService(running._id, {
        task_description: parsed.task,
        project: parsed.projectId,
      })
        .then((res) => {
          const items = res.data?.items;
          const updated = Array.isArray(items) ? items[0] : null;
          if (updated) setRunningEntry(updated);
          else loadRunning();
        })
        .catch(handleApiError);
    }
  }, []);

  const handleDescriptionEnter = () => {
    const running = runningEntryRef.current;
    if (running) {
      const next = (descriptionBridge.current?.get?.() ?? '').trim();
      const current = (running.task_description ?? '').trim();
      if (!running.pending && next !== current) {
        saveRunningDescription(running._id, next);
      }
    } else {
      handleStart();
    }
  };

  const applyRunningStartTime = useCallback(() => {
    if (!runningEntry?._id || runningEntry.pending) return;
    const day = moment().clone().startOf('day');
    const merged = mergeTimeStringIntoMoment(day, runningStartTimeStr);
    if (!merged) {
      message.error('Invalid time. Example: 3:35PM or 4:20 PM');
      return;
    }
    if (merged.valueOf() >= Date.now()) {
      message.error('Start time must be before the current time');
      return;
    }
    setSavingRunningStart(true);
    timeTrackerEntryUpdateService(runningEntry._id, {
      started_at: merged.toISOString(),
    })
      .then((res) => {
        const items = res.data?.items;
        const updated = Array.isArray(items) ? items[0] : null;
        if (updated) setRunningEntry(updated);
        else loadRunning();
        setSavingRunningStart(false);
        setStartTimePopoverOpen(false);
        handleApiSuccess(res);
      })
      .catch((err) => {
        setSavingRunningStart(false);
        handleApiError(err);
      });
  }, [runningEntry, runningStartTimeStr]);

  const runningStartPopoverContent = useMemo(
    () => (
      <div className="clockify-running-start-popover">
        <div className="clockify-running-start-popover-label">START TIME</div>
        <div className="clockify-running-start-popover-row">
          <Input
            className="clockify-running-start-popover-time"
            value={runningStartTimeStr}
            onChange={(e) => setRunningStartTimeStr(e.target.value)}
            onPressEnter={applyRunningStartTime}
            aria-label="Start time"
          />
          <Text
            type="secondary"
            className="clockify-running-start-popover-today"
          >
            Today
          </Text>
        </div>
        <Button
          type="primary"
          size="small"
          block
          style={{ marginTop: 12 }}
          loading={savingRunningStart}
          onClick={applyRunningStartTime}
        >
          Save start time
        </Button>
      </div>
    ),
    [runningStartTimeStr, savingRunningStart, applyRunningStartTime],
  );

  if (permissions == null) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!accessControls.can(permissions, 'TimeTracker.read:any')) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="403"
          title="Access denied"
          subTitle="You do not have permission to view Time Tracker."
          extra={
            <Button type="primary" onClick={() => history.push('/')}>
              Go to Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  const isRunning = !!runningEntry;

  const runningProjectId = runningEntry?.project?._id || runningEntry?.project;

  const historyPager = (
    <Space size="small" className="clockify-history-pager" wrap>
      <Button
        size="small"
        disabled={historyPage === 0 || entriesLoading}
        onClick={() => setHistoryPage((page) => Math.max(0, page - 1))}
      >
        Newer
      </Button>
      <Text type="secondary" className="clockify-history-range">
        {historyRangeLabel}
      </Text>
      <Button
        size="small"
        disabled={entriesLoading}
        onClick={() => setHistoryPage((page) => page + 1)}
      >
        Older 4 weeks
      </Button>
    </Space>
  );

  return (
    <div className="clockify-tracker">
      {accessControls.can(permissions, 'Scheduling.read:any') &&
        todayShifts.length > 0 &&
        !isRunning && (
          <div className="clockify-today-schedule">
            <div className="clockify-today-schedule-label">
              <CalendarOutlined /> Today’s schedule
            </div>
            <div className="clockify-today-schedule-chips">
              {todayShifts.map((shift) => {
                const name = shift.activity?.name || 'Shift';
                const start = moment
                  .tz(shift.starts_at, effectiveTz)
                  .format('h:mm A');
                const end = moment
                  .tz(shift.ends_at, effectiveTz)
                  .format('h:mm A');
                const selected = String(linkedShiftId) === String(shift._id);
                return (
                  <button
                    key={shift._id}
                    type="button"
                    className={
                      'clockify-today-schedule-chip' +
                      (selected
                        ? ' clockify-today-schedule-chip--selected'
                        : '')
                    }
                    onClick={() => {
                      descriptionBridge.current?.set?.(name);
                      setLinkedShiftId(String(shift._id));
                    }}
                  >
                    <span
                      className="clockify-today-schedule-dot"
                      style={{
                        background:
                          shift.color || shift.activity?.color || '#1890ff',
                      }}
                    />
                    <span className="clockify-today-schedule-chip-text">
                      {name} · {start}–{end}
                    </span>
                    <Button
                      type="link"
                      size="small"
                      className="clockify-today-schedule-start"
                      loading={startingFromShiftId === shift._id}
                      onClick={(e) => {
                        e.stopPropagation();
                        startFromScheduledShift(shift);
                      }}
                    >
                      Start
                    </Button>
                  </button>
                );
              })}
            </div>
          </div>
        )}

      {/* Timer bar — Clockify style */}
      <div className="clockify-timer-bar">
        <div className="clockify-description-input-wrap">
          <TimerDescriptionField
            ref={descriptionBridge}
            entryId={runningEntry?._id || null}
            serverText={runningEntry?.task_description ?? ''}
            pending={!!runningEntry?.pending}
            taskAutocompleteOptions={taskAutocompleteOptions}
            todayShiftCount={todayShifts.length}
            onSaveDescription={saveRunningDescription}
            onEnter={handleDescriptionEnter}
            onClearLink={clearLinkedShift}
            onPickSchedule={onPickSchedule}
            onPickTask={onPickTask}
          />
          {linkedShift && (
            <Text type="secondary" className="clockify-linked-shift-hint">
              Linked to schedule: {linkedShift.activity?.name || 'shift'} ·{' '}
              {moment.tz(linkedShift.starts_at, effectiveTz).format('h:mm A')}–
              {moment.tz(linkedShift.ends_at, effectiveTz).format('h:mm A')}
            </Text>
          )}
        </div>
        <Select
          className="clockify-project-select"
          placeholder="Task @ Project (optional)"
          value={selectedProjectId || undefined}
          onChange={(v) => {
            const next = v || null;
            setSelectedProjectId(next);
            if (runningEntry?._id && !runningEntry.pending) {
              timeTrackerEntryUpdateService(runningEntry._id, {
                project: next,
              })
                .then((res) => {
                  const items = res.data?.items;
                  const updated = Array.isArray(items) ? items[0] : null;
                  if (updated) setRunningEntry(updated);
                  else loadRunning();
                })
                .catch(handleApiError);
            }
          }}
          showSearch
          filterOption={filterProjectSelectOption}
          options={projectOptions}
          notFoundContent={
            projects.length === 0 ? 'No projects' : 'No matching projects'
          }
          dropdownMatchSelectWidth={400}
          dropdownStyle={{ maxHeight: 320 }}
          allowClear
        />
        {isRunning ? (
          <Popover
            content={runningStartPopoverContent}
            trigger="click"
            visible={startTimePopoverOpen}
            onVisibleChange={setStartTimePopoverOpen}
            placement="bottomRight"
            overlayClassName="clockify-running-start-popover-overlay"
            getPopupContainer={(n) => n.parentElement || document.body}
          >
            <span
              className="clockify-timer-display clockify-timer-display--clickable"
              title="Edit start time"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setStartTimePopoverOpen(true);
                }
              }}
            >
              <LiveElapsed startedAt={runningEntry.started_at} />
            </span>
          </Popover>
        ) : (
          <span className="clockify-timer-display">00:00:00</span>
        )}
        {isRunning ? (
          <Button
            type="primary"
            className="clockify-start-btn"
            onClick={(e) => {
              // Stopping the timer must require an actual pointer click.
              // `detail === 0` means the click was synthesized from a keyboard
              // activation (Enter / Space while the button was focused); we
              // ignore those so an Enter key-press can never end a session.
              if (e && e.detail === 0) {
                e.preventDefault();
                e.currentTarget.blur();
                return;
              }
              handleStopClick();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            onKeyUp={(e) => {
              if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            loading={stopSubmitting}
          >
            STOP
          </Button>
        ) : (
          <Button
            type="primary"
            className="clockify-start-btn"
            onClick={handleStart}
            loading={timerStarting || stopSubmitting}
            disabled={stopSubmitting}
          >
            START
          </Button>
        )}
        <div className="clockify-timer-bar-end">
          <Popover
            title="Time zone"
            placement="bottomRight"
            overlayClassName="clockify-tz-popover-overlay"
            trigger="click"
            getPopupContainer={() => document.body}
            content={
              <div className="clockify-tz-popover-body">
                <Text type="secondary" className="clockify-tz-popover-summary">
                  <GlobalOutlined aria-hidden /> {trackerTzSummary}
                </Text>
                <Text type="secondary" className="clockify-tz-popover-hint">
                  “This week”, “Last week”, and “Today” totals use this zone.
                </Text>
                <Select
                  size="small"
                  showSearch
                  allowClear
                  dropdownStyle={{ maxHeight: 280 }}
                  placeholder="Search time zones…"
                  value={trackerTimezoneStored}
                  options={timezoneSelectOptions}
                  onChange={(v) => {
                    const next = v === undefined || v === null ? '' : v;
                    setTrackerTimezoneStored(next);
                    saveStoredTimezone(next);
                  }}
                  optionFilterProp="label"
                  getPopupContainer={() => document.body}
                  className="clockify-tz-popover-select"
                  aria-label="Time tracker timezone"
                />
              </div>
            }
          >
            <Button
              type="text"
              className="clockify-tz-trigger"
              icon={<GlobalOutlined aria-hidden />}
              aria-label="Time zone for weeks and today — click to change"
            >
              <span className="clockify-tz-trigger-text">
                {trackerTzCompact}
              </span>
            </Button>
          </Popover>
          {accessControls.can(permissions, 'Scheduling.read:any') && (
            <Tooltip title="My schedule">
              <span
                className="clockify-bar-icon"
                title="My schedule"
                onClick={() => setScheduleCalendarVisible(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === 'Enter' && setScheduleCalendarVisible(true)
                }
              >
                <CalendarOutlined />
              </span>
            </Tooltip>
          )}
          <span
            className="clockify-bar-icon"
            title="Add time manually"
            onClick={() => setManualVisible(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setManualVisible(true)}
          >
            <ClockCircleOutlined />
          </span>
        </div>
      </div>

      {/* Last N weeks — Mon–Sun, week starts Mon 00:01 in selected (or browser) timezone */}
      <div className="clockify-entries">
        <div className="clockify-section">
          <div className="clockify-section-header">
            <div className="clockify-section-title-block">
              <Text strong className="clockify-section-title">
                {historyPage === 0 ? 'Last 4 weeks' : 'Earlier weeks'}
              </Text>
              <Text type="secondary" className="clockify-section-subtitle">
                {historyRangeLabel} · Monday → Sunday
              </Text>
            </div>
            <Space size="middle" wrap>
              {historyPager}
              <Text type="secondary" className="clockify-section-total">
                4-week total: {formatDuration(rangeTotalSeconds)}
              </Text>
              {historyPage === 0 ? (
                <Text type="secondary">
                  Today ({trackerTzCompact}): {formatDuration(todayTotalSeconds)}
                </Text>
              ) : null}
              <Tooltip title="Refresh projects, timer, and entries">
                <Button
                  type="text"
                  size="small"
                  className="clockify-header-refresh"
                  icon={<ReloadOutlined />}
                  loading={headerRefreshLoading}
                  onClick={refreshAllTrackerData}
                  aria-label="Refresh all"
                />
              </Tooltip>
              <CopyOutlined className="clockify-copy-icon" />
            </Space>
          </div>

          {entriesLoading ? (
            <div className="clockify-entries-loading">
              <Spin />
            </div>
          ) : weeksGrouped.length === 0 ? (
            <div className="clockify-entry-empty">
              No time entries in {historyRangeLabel}. Start the timer above, or
              open older weeks.
            </div>
          ) : (
            weeksGrouped.map((week, weekIdx) => {
              const runningWeekKey = runningEntry
                ? getWeekStartMoment(
                    new Date(runningEntry.started_at),
                    effectiveTz,
                  ).valueOf()
                : null;
              const showRunningHere =
                runningEntry && runningWeekKey === week.weekStartMs;
              const stoppedRows = week.entries.filter(
                (e) => e._id !== runningEntry?._id,
              );
              const isCurrentWeek = historyPage === 0 && weekIdx === 0;
              return (
                <div
                  key={week.weekStartMs}
                  className={`clockify-week-block${
                    isCurrentWeek ? ' clockify-week-block-current' : ''
                  }`}
                >
                  <div className="clockify-week-heading">
                    <div className="clockify-week-title-block">
                      <Text strong className="clockify-week-title">
                        {week.heading.title}
                      </Text>
                      <Text
                        type="secondary"
                        className="clockify-week-title-range"
                      >
                        {week.heading.range}
                      </Text>
                    </div>
                    <Text type="secondary" className="clockify-week-total">
                      Week total: {formatDuration(week.totalSeconds)}
                    </Text>
                  </div>
                  <div className="clockify-entry-list">
                    {showRunningHere && (
                      <div className="clockify-entry-row clockify-entry-row-running">
                        <span className="clockify-entry-desc">
                          {runningEntry.task_description || 'Add description'}
                        </span>
                        <span className="clockify-entry-project">
                          <span
                            className="clockify-project-dot"
                            style={{
                              background: runningEntry.project
                                ? getProjectColor(runningEntry.project)
                                : getNoProjectColor(),
                            }}
                          />
                          {runningEntry.project?.project_name || 'No project'}
                        </span>
                        <span className="clockify-entry-time">
                          {formatTimeOnly(runningEntry.started_at)} - ...
                        </span>
                        <Tooltip
                          title={formatEntryDateForTz(
                            runningEntry.started_at,
                            effectiveTz,
                            'full',
                          )}
                          placement="top"
                        >
                          <span
                            className="clockify-entry-date-chip clockify-entry-date-chip-static"
                            aria-label={`Started on ${formatEntryDateForTz(
                              runningEntry.started_at,
                              effectiveTz,
                              'full',
                            )}`}
                          >
                            <CalendarOutlined className="clockify-entry-date-chip-icon" />
                            <span className="clockify-entry-date-chip-text">
                              {formatEntryDateForTz(
                                runningEntry.started_at,
                                effectiveTz,
                                'short',
                              )}
                            </span>
                          </span>
                        </Tooltip>
                        <LiveElapsed
                          startedAt={runningEntry.started_at}
                          className="clockify-entry-duration"
                        />
                      </div>
                    )}
                    {stoppedRows.map((entry) => {
                      const pid = entry.project?._id || entry.project;
                      const isRunningThis =
                        !!runningEntry &&
                        runningProjectId != null &&
                        pid != null &&
                        String(runningProjectId) === String(pid);
                      return (
                        <StoppedEntryRow
                          key={entry._id}
                          entry={entry}
                          isRunningThis={isRunningThis}
                          resumeLoading={resumeLoadingEntryId === entry._id}
                          onResume={handleResumeEntry}
                          onReload={reloadEntriesQuiet}
                          effectiveTz={effectiveTz}
                          optionsRef={listOptionsRef}
                        />
                      );
                    })}
                    {!showRunningHere && stoppedRows.length === 0 && (
                      <div className="clockify-entry-empty clockify-week-empty">
                        No entries this week.
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
          {!entriesLoading ? (
            <div className="clockify-history-pager-footer">{historyPager}</div>
          ) : null}
        </div>
      </div>

      {/* Manual time entry modal — only optional task_description */}
      <Modal
        title="Add time manually"
        open={manualVisible}
        onOk={handleManualSubmit}
        onCancel={() => {
          setManualVisible(false);
          manualForm.resetFields();
        }}
        confirmLoading={manualSubmitting}
        okText="Add entry"
        width={480}
      >
        <Form form={manualForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="started_at"
            label="Start date & time"
            rules={[{ required: true, message: 'Select start time' }]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="stopped_at"
            label="Stop date & time"
            rules={[{ required: true, message: 'Select stop time' }]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="task_description"
            label="Task description (optional)"
          >
            <Input.TextArea rows={3} placeholder="What did you work on?" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Link timer to a scheduled shift?"
        open={shiftPickerVisible}
        visible={shiftPickerVisible}
        okText="Start timer"
        confirmLoading={timerStarting}
        onCancel={() => setShiftPickerVisible(false)}
        onOk={() =>
          startTimer(
            clockShifts.find(
              (shift) => String(shift._id) === selectedClockShiftId,
            ),
          )
        }
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item
            label="Today’s shifts"
            extra="Choose a shift so the timer is categorized correctly, or continue without linking."
          >
            <Select
              style={{ width: '100%' }}
              value={selectedClockShiftId}
              onChange={setSelectedClockShiftId}
              placeholder="Select a shift (optional)"
              options={[
                { value: '', label: 'Not linked to a shift' },
                ...clockShifts.map((shift) => ({
                  value: String(shift._id),
                  label: `${shift.activity?.name || 'Shift'} · ${moment
                    .tz(shift.starts_at, effectiveTz)
                    .format('h:mm A')} – ${moment
                    .tz(shift.ends_at, effectiveTz)
                    .format('h:mm A')}`,
                })),
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="My schedule"
        open={scheduleCalendarVisible}
        visible={scheduleCalendarVisible}
        onCancel={() => setScheduleCalendarVisible(false)}
        footer={null}
        width={640}
        destroyOnClose
      >
        {scheduleWeekStart && (
          <MyScheduleCalendar
            shifts={myShifts}
            timezone={effectiveTz}
            weekStart={scheduleWeekStart}
            onPrevWeek={() =>
              setScheduleWeekStart((w) => w.clone().subtract(7, 'days'))
            }
            onNextWeek={() =>
              setScheduleWeekStart((w) => w.clone().add(7, 'days'))
            }
            onThisWeek={() =>
              setScheduleWeekStart(getWeekStartMoment(moment(), effectiveTz))
            }
            onStartShift={startFromScheduledShift}
            startingShiftId={startingFromShiftId}
            disabled={!!runningEntry}
          />
        )}
      </Modal>
    </div>
  );
}
